/**
 * Document Context Store
 *
 * Manages uploaded documents and their extracted text content
 * that serves as context for the AI assistant.
 *
 * Features:
 * - Document upload and storage
 * - Per-document selection for voice context
 * - 200K character limit with warnings
 * - Session persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UploadedDocument {
  id: string;
  name: string;
  type: 'pdf' | 'docx' | 'txt' | 'xlsx' | 'csv' | 'image';
  size: number;
  uploadedAt: Date;
  extractedText?: string;
  preview?: string; // For images, base64 thumbnail
  pageCount?: number; // For PDFs
}

// Token limit constants
export const MAX_CONTEXT_CHARACTERS = 200000; // 200K character limit
export const WARNING_THRESHOLD = 150000; // 150K - show warning
export const DANGER_THRESHOLD = 200000; // 200K - show error

interface DocumentContextState {
  documents: UploadedDocument[];
  selectedDocumentIds: string[]; // IDs of documents selected for voice context
  totalCharacters: number;
  selectedCharacters: number;
  maxCharacters: number;

  // Actions
  addDocument: (doc: UploadedDocument) => void;
  removeDocument: (id: string) => void;
  clearAllDocuments: () => void;
  getContextText: () => string;
  updateDocumentText: (id: string, text: string) => void;

  // Selection actions
  toggleDocumentSelection: (id: string) => void;
  selectAllDocuments: () => void;
  clearSelection: () => void;
  isDocumentSelected: (id: string) => boolean;
  getSelectedContextText: () => string;

  // Helpers
  getSelectionStatus: () => 'safe' | 'warning' | 'danger';
}

// Helper to calculate character counts from documents
const calculateCharacterCounts = (
  documents: UploadedDocument[],
  selectedDocumentIds: string[]
) => {
  const totalCharacters = documents.reduce(
    (sum, d) => sum + (d.extractedText?.length || 0),
    0
  );
  const selectedCharacters = documents
    .filter(d => selectedDocumentIds.includes(d.id))
    .reduce((sum, d) => sum + (d.extractedText?.length || 0), 0);
  return { totalCharacters, selectedCharacters };
};

export const useDocumentContextStore = create<DocumentContextState>()(
  persist(
    (set, get) => ({
      documents: [],
      selectedDocumentIds: [],
      totalCharacters: 0,
      selectedCharacters: 0,
      maxCharacters: MAX_CONTEXT_CHARACTERS,

      addDocument: (doc) => {
        set((state) => {
          const newDocs = [...state.documents, doc];
          const totalChars = newDocs.reduce(
            (sum, d) => sum + (d.extractedText?.length || 0),
            0
          );
          // Auto-select new document if within limit
          const docChars = doc.extractedText?.length || 0;
          const newSelectedChars = state.selectedCharacters + docChars;
          const shouldAutoSelect = newSelectedChars <= MAX_CONTEXT_CHARACTERS;

          return {
            documents: newDocs,
            totalCharacters: totalChars,
            selectedDocumentIds: shouldAutoSelect
              ? [...state.selectedDocumentIds, doc.id]
              : state.selectedDocumentIds,
            selectedCharacters: shouldAutoSelect
              ? newSelectedChars
              : state.selectedCharacters,
          };
        });
      },

      removeDocument: (id) => {
        set((state) => {
          const newDocs = state.documents.filter(d => d.id !== id);
          const totalChars = newDocs.reduce(
            (sum, d) => sum + (d.extractedText?.length || 0),
            0
          );
          const newSelectedIds = state.selectedDocumentIds.filter(docId => docId !== id);
          const selectedChars = newDocs
            .filter(d => newSelectedIds.includes(d.id))
            .reduce((sum, d) => sum + (d.extractedText?.length || 0), 0);

          return {
            documents: newDocs,
            totalCharacters: totalChars,
            selectedDocumentIds: newSelectedIds,
            selectedCharacters: selectedChars,
          };
        });
      },

      clearAllDocuments: () => {
        set({
          documents: [],
          totalCharacters: 0,
          selectedDocumentIds: [],
          selectedCharacters: 0,
        });
      },

      // Get all document context (legacy - for backward compatibility)
      getContextText: () => {
        const { documents } = get();
        if (documents.length === 0) return '';

        const contextParts = documents
          .filter(d => d.extractedText)
          .map(d => `=== FILE: ${d.name} ===\n${d.extractedText}`);

        return contextParts.join('\n\n');
      },

      // Get ONLY selected documents context (for voice bot)
      getSelectedContextText: () => {
        const { documents, selectedDocumentIds } = get();
        if (selectedDocumentIds.length === 0) return '';

        const selectedDocs = documents.filter(d =>
          selectedDocumentIds.includes(d.id) && d.extractedText
        );

        if (selectedDocs.length === 0) return '';

        const contextParts = selectedDocs
          .map(d => `=== FILE: ${d.name} ===\n${d.extractedText}`);

        return contextParts.join('\n\n');
      },

      updateDocumentText: (id, text) => {
        set((state) => {
          const newDocs = state.documents.map(d =>
            d.id === id ? { ...d, extractedText: text } : d
          );
          const totalChars = newDocs.reduce(
            (sum, d) => sum + (d.extractedText?.length || 0),
            0
          );
          const selectedChars = newDocs
            .filter(d => state.selectedDocumentIds.includes(d.id))
            .reduce((sum, d) => sum + (d.extractedText?.length || 0), 0);

          return {
            documents: newDocs,
            totalCharacters: totalChars,
            selectedCharacters: selectedChars,
          };
        });
      },

      // Toggle document selection for voice context
      toggleDocumentSelection: (id) => {
        set((state) => {
          const isCurrentlySelected = state.selectedDocumentIds.includes(id);
          const doc = state.documents.find(d => d.id === id);
          const docChars = doc?.extractedText?.length || 0;

          if (isCurrentlySelected) {
            // Deselect - always allowed
            const newSelectedIds = state.selectedDocumentIds.filter(docId => docId !== id);
            return {
              selectedDocumentIds: newSelectedIds,
              selectedCharacters: state.selectedCharacters - docChars,
            };
          } else {
            // Select - add to selection (allow even if over limit, just warn)
            return {
              selectedDocumentIds: [...state.selectedDocumentIds, id],
              selectedCharacters: state.selectedCharacters + docChars,
            };
          }
        });
      },

      selectAllDocuments: () => {
        set((state) => {
          const allIds = state.documents.map(d => d.id);
          const totalSelected = state.documents.reduce(
            (sum, d) => sum + (d.extractedText?.length || 0),
            0
          );
          return {
            selectedDocumentIds: allIds,
            selectedCharacters: totalSelected,
          };
        });
      },

      clearSelection: () => {
        set({
          selectedDocumentIds: [],
          selectedCharacters: 0,
        });
      },

      isDocumentSelected: (id) => {
        return get().selectedDocumentIds.includes(id);
      },

      // Get selection status based on character count
      getSelectionStatus: () => {
        const { selectedCharacters } = get();
        if (selectedCharacters >= DANGER_THRESHOLD) return 'danger';
        if (selectedCharacters >= WARNING_THRESHOLD) return 'warning';
        return 'safe';
      },
    }),
    {
      name: 'dryvox-document-context',
      // BUG-027: Don't persist extractedText at all. Previously sliced to 10K chars
      // which silently corrupted document context on page reload. Now we persist only
      // metadata (name, type, size) and selections. On reload, documents show as
      // "needs re-upload" — explicit and honest rather than silently truncated.
      partialize: (state) => ({
        documents: state.documents.map(d => ({
          id: d.id,
          name: d.name,
          type: d.type,
          size: d.size,
          uploadedAt: d.uploadedAt,
          pageCount: d.pageCount,
          // extractedText and preview deliberately NOT persisted
        })),
        selectedDocumentIds: state.selectedDocumentIds,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // After hydration, documents have no extractedText — mark them clearly
          const docsNeedingReupload = state.documents.filter(d => !d.extractedText);
          if (docsNeedingReupload.length > 0) {
            console.log(
              `[DocumentContext] Hydrated: ${state.documents.length} docs need re-upload ` +
              `(text not persisted to avoid silent truncation)`
            );
          }
          // Character counts will be 0 since no text is persisted
          useDocumentContextStore.setState({
            totalCharacters: 0,
            selectedCharacters: 0,
          });
        }
      },
    }
  )
);

export default useDocumentContextStore;
